import { useEffect, useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { ScrollText, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Tag, Spinner, EmptyState } from "@/components/shared";

const LIMIT = 50;

const ACTION_TONE = {
  ban: "red",
  unban: "green",
  warn: "amber",
  verify: "green",
  unverify: "muted",
  grant_premium: "amber",
  revoke_premium: "muted",
  delete_user: "red",
  remove_photo: "amber",
  resolve_report: "blue",
  review_verification: "blue",
};

function fmt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return dt;
  }
}

export default function Audit() {
  const [data, setData] = useState({ actions: [], total: 0 });
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.audit({ skip, limit: LIMIT });
      setData(res);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [skip]);

  useEffect(() => {
    load();
  }, [load]);

  const total = data.total || 0;
  const page = Math.floor(skip / LIMIT) + 1;
  const pages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div>
      <PageHeader title="Audit Log" subtitle={`${total} recorded admin actions`} />

      <div className="rounded-2xl bg-card ring-1 ring-border/70 overflow-x-auto">
        {loading ? (
          <Spinner />
        ) : data.actions.length === 0 ? (
          <EmptyState icon={ScrollText} title="No actions logged" />
        ) : (
          <Table data-testid="audit-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border/60">
                <TableHead>Action</TableHead>
                <TableHead>Admin</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.actions.map((a) => (
                <TableRow key={a.action_id} className="border-border/50" data-testid={`audit-${a.action_id}`}>
                  <TableCell>
                    <Tag tone={ACTION_TONE[a.action] || "muted"}>{a.action}</Tag>
                  </TableCell>
                  <TableCell className="text-sm">{a.admin_name || a.admin_id}</TableCell>
                  <TableCell className="text-sm">
                    <div className="truncate max-w-[180px]">{a.target_name || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate max-w-[180px]">{a.target_id}</div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate">
                    {a.reason || "—"}
                  </TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground whitespace-nowrap">
                    {fmt(a.created_at)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {total > LIMIT && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <span className="text-muted-foreground">Page {page} of {pages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - LIMIT))} data-testid="audit-prev">
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setSkip(skip + LIMIT)} data-testid="audit-next">
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
